import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { FuelRobDataTypeEnum, FuelTypeEnum, NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { FuelRobPerTank } from '../../../../types/ViewModel/FuelRobPerTank';
import { NoonReportSectionData } from '../../../../types/ViewModel/NoonReportSectionData';
import {
    getBunkeringValue,
    getFuelTypeString,
    getPreviousRobValue,
    getRobValue,
    hasInvalidFields,
    hasMissingValues,
} from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import { ListHeaderStyle, SubSectionHeader } from '../../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { GridWrapper, TankWrapper, Wrapper } from '../FuelRobSection.style';
import { truncateTo2Decimals } from '../../../../utils/numberHelper';
import { useVesselStore } from '../../../../context/VesselContext';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';

type Props = {
    vm: any;
    fuelRobVM: any;
    fuelRobPerTanks: FuelRobPerTank[];
    fuelType: FuelTypeEnum;
    fuelRobDataType: FuelRobDataTypeEnum;
    portName: string;
    unit: string;
    hasEmptyValues;
    sectionData?: NoonReportSectionData;
    aolDataItem: AolDataItem | null;
    isReadOnly: boolean;
    updatePreviousROB: (fuelRobVm: any, fuelRobPerTankUpdate: FuelRobPerTank) => void;
};

const FuelRobBunkerComponent: React.FC<Props> = (props) => {
    const [headerString, setHeaderString] = useState('');
    const [convertedFuelRobPerTanks, setConvertedFuelRobPerTanks] = useState<FuelRobPerTank[]>([]);
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const { saveAolEvent } = useAolSaveHandler();
    const { vessel } = useVesselStore();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.FuelRob &&
            props.aolDataItem &&
            saveAolEvent.aolDataItem.id === props.aolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') checkOnSave();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!props) return;
        initVM();
    }, [props]);

    const initVM = () => {
        setConvertedFuelRobPerTanks([...props.fuelRobPerTanks]);
        setHeaderString(getHeader());
        setHasEmptyValuesLocal(false);
    };

    const getHeader = () => {
        let headerString = `${getFuelTypeString(props.fuelType)} after bunkering`;

        if (props.fuelRobVM.eventDateTime) {
            headerString += ` ${prettifyDateTimeHHmm(props.fuelRobVM.eventDateTime)}`;
        }

        return headerString;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        changeObject.dataObject[name] = value ? +value : undefined;

        if (name === 'bunkeringInM3' && changeObject.dataObject[name] !== undefined && changeObject.dataObject['litreWeight'] !== undefined) {
            changeObject.dataObject['bunkeringInMt'] = truncateTo2Decimals(
                changeObject.dataObject['bunkeringInM3'] * changeObject.dataObject['litreWeight']
            );
        }
        if (name === 'bunkeringInMt' && changeObject.dataObject[name] !== undefined && changeObject.dataObject['litreWeight'] > 0) {
            changeObject.dataObject['bunkeringInM3'] = truncateTo2Decimals(
                changeObject.dataObject['bunkeringInMt'] / changeObject.dataObject['litreWeight']
            );
        }
        if (name === 'robInM3' && changeObject.dataObject[name] !== undefined && changeObject.dataObject['litreWeight'] !== undefined) {
            changeObject.dataObject['robInMt'] = truncateTo2Decimals(changeObject.dataObject['robInM3'] * changeObject.dataObject['litreWeight']);
            props.updatePreviousROB(props.fuelRobVM, changeObject.dataObject);
        }
        if (name === 'robInMt' && changeObject.dataObject[name] !== undefined && changeObject.dataObject['litreWeight'] > 0) {
            changeObject.dataObject['robInM3'] = truncateTo2Decimals(changeObject.dataObject['robInMt'] / changeObject.dataObject['litreWeight']);
            props.updatePreviousROB(props.fuelRobVM, changeObject.dataObject);
        }
        if (name === 'litreWeight' && changeObject.dataObject[name] !== undefined && changeObject.dataObject['litreWeight'] > 0) {
            if (props.unit === 'm3') {
                changeObject.dataObject['bunkeringInMt'] = truncateTo2Decimals(
                    changeObject.dataObject['bunkeringInM3'] * changeObject.dataObject['litreWeight']
                );
                changeObject.dataObject['robInMt'] = truncateTo2Decimals(changeObject.dataObject['robInM3'] * changeObject.dataObject['litreWeight']);
            } else {
                changeObject.dataObject['bunkeringInM3'] = truncateTo2Decimals(
                    changeObject.dataObject['bunkeringInMt'] / changeObject.dataObject['litreWeight']
                );
                changeObject.dataObject['robInM3'] = truncateTo2Decimals(changeObject.dataObject['robInMt'] / changeObject.dataObject['litreWeight']);
            }
        }

        hasInvalidFields(props.vm, name, changeObject.isValid);
        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
        setConvertedFuelRobPerTanks([...props.fuelRobPerTanks]);
    };

    const checkOnSave = () => {
        const ignoreList: string[] = [];
        if (props.unit !== 'm3') {
            ignoreList.push('bunkeringInM3');
            ignoreList.push('robInM3');
        } else {
            ignoreList.push('bunkeringInMt');
            ignoreList.push('robInMt');
        }
        const hasMissing = props.fuelRobPerTanks.some((rob) => {
            return hasMissingValues(rob, ignoreList);
        });

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            props.hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            props.hasEmptyValues.current = false;
        }
    };
    const getMaxTankWeight = (fuelRobPerTank: FuelRobPerTank): number => {
        return truncateTo2Decimals(
            fuelRobPerTank !== undefined && fuelRobPerTank.tankVolume > 0 && fuelRobPerTank.litreWeight > 0
                ? fuelRobPerTank.tankVolume * fuelRobPerTank.litreWeight
                : 10000
        );
    };

    const getMaxTankVolume = (fuelRobPerTank: FuelRobPerTank): number => {
        return fuelRobPerTank?.tankVolume > 0 ? fuelRobPerTank.tankVolume : 10000;
    };
    const getMaxTankWeightOrVolume = (fuelRobPerTank: FuelRobPerTank): number => {
        return props.unit === 'm3' ? getMaxTankVolume(fuelRobPerTank) : getMaxTankWeight(fuelRobPerTank);
    };

    const getMaxSulphurPercent = () => {
        if (vessel?.isEquippedWithEgcs) {
            if (props.fuelType === FuelTypeEnum.HFO) return 5;
            if (props.fuelType === FuelTypeEnum.MGO) return 1;
        }
        return 1;
    };

    const getTotalRobValue = () => {
        return props.fuelRobPerTanks.reduce((a, b) => a + getRobValue(b, props.unit), 0);
    };

    const getCorrection = () => {
        return props.fuelRobPerTanks.reduce(
            (a, b) => a + getPreviousRobValue(b, props.unit) - getRobValue(b, props.unit) + getBunkeringValue(b, props.unit),
            0
        );
    };

    const hasCorrectionError = () => {
        const correction = getCorrection();
        if (correction > 0) {
            const totalRobValue = getTotalRobValue();
            if (totalRobValue > 0) {
                return Math.abs(correction / totalRobValue) > 0.05;
            }
        }
    };

    return (
        <>
            {props.fuelRobPerTanks && (
                <Wrapper>
                    <SubSectionHeader className="flex-space-between">
                        <span>{headerString}</span>
                        <span>
                            {getTotalRobValue().toFixed(2)} {props.unit}
                        </span>
                    </SubSectionHeader>
                    <TankWrapper>
                        <GridWrapper gridCols={5}>
                            <ListHeaderStyle>
                                <li>
                                    <span className="col-tank">Tank</span>
                                    <span className="col-rob">Bunkering</span>
                                    <span>Sulphur %</span>
                                    <span>Litre weight</span>
                                    <span className="col-rob">ROB</span>
                                </li>
                            </ListHeaderStyle>
                            {convertedFuelRobPerTanks.map((fuelRobPerTank) => (
                                <li key={fuelRobPerTank.tankId}>
                                    <span className="col-tank">{fuelRobPerTank.tankName} </span>
                                    {props.unit !== 'm3' && (
                                        <span>
                                            <OdfjellInputComponent
                                                isReadOnly={props.isReadOnly}
                                                type={'number'}
                                                value={fuelRobPerTank.bunkeringInMt}
                                                name={'bunkeringInMt'}
                                                min={0}
                                                max={getMaxTankWeightOrVolume(fuelRobPerTank)}
                                                dataObject={fuelRobPerTank}
                                                onChange={onInputChange}
                                            />
                                        </span>
                                    )}
                                    {props.unit === 'm3' && (
                                        <span>
                                            <OdfjellInputComponent
                                                isReadOnly={props.isReadOnly}
                                                type={'number'}
                                                value={fuelRobPerTank.bunkeringInM3}
                                                name={'bunkeringInM3'}
                                                min={0}
                                                max={getMaxTankWeightOrVolume(fuelRobPerTank)}
                                                dataObject={fuelRobPerTank}
                                                onChange={onInputChange}
                                            />
                                        </span>
                                    )}
                                    <span>
                                        <OdfjellInputComponent
                                            isReadOnly={props.isReadOnly}
                                            type={'number'}
                                            value={fuelRobPerTank.sulphurPercent}
                                            min={0}
                                            max={getMaxSulphurPercent()}
                                            name={'sulphurPercent'}
                                            dataObject={fuelRobPerTank}
                                            onChange={onInputChange}
                                        />
                                    </span>
                                    <span>
                                        <OdfjellInputComponent
                                            isReadOnly={props.isReadOnly}
                                            type={'number'}
                                            value={fuelRobPerTank.litreWeight}
                                            min={0}
                                            max={2}
                                            name={'litreWeight'}
                                            dataObject={fuelRobPerTank}
                                            onChange={onInputChange}
                                        />
                                    </span>
                                    {props.unit !== 'm3' && (
                                        <span className="col-rob">
                                            <OdfjellInputComponent
                                                isReadOnly={props.isReadOnly}
                                                type={'number'}
                                                value={fuelRobPerTank.robInMt}
                                                name={'robInMt'}
                                                min={0}
                                                max={getMaxTankWeightOrVolume(fuelRobPerTank)}
                                                dataObject={fuelRobPerTank}
                                                onChange={onInputChange}
                                            />
                                        </span>
                                    )}
                                    {props.unit === 'm3' && (
                                        <span className="col-rob">
                                            <OdfjellInputComponent
                                                isReadOnly={props.isReadOnly}
                                                type={'number'}
                                                value={fuelRobPerTank.robInM3}
                                                name={'robInM3'}
                                                min={0}
                                                max={getMaxTankWeightOrVolume(fuelRobPerTank)}
                                                dataObject={fuelRobPerTank}
                                                onChange={onInputChange}
                                            />
                                        </span>
                                    )}
                                </li>
                            ))}
                        </GridWrapper>
                    </TankWrapper>
                    {hasEmptyValuesLocal && <ErrorText>You must enter all data before saving.</ErrorText>}
                    {hasCorrectionError() && (
                        <ErrorText>
                            {getFuelTypeString(props.fuelType)} correction: {getCorrection().toFixed(2)}
                            {props.unit}
                        </ErrorText>
                    )}
                </Wrapper>
            )}
        </>
    );
};

export default FuelRobBunkerComponent;
